import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Alert,
  AlertTitle,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import {
  ElectricBolt as SolarIcon,
  LocationOn as LocationIcon,
  Group as ReferralIcon,
  AccountBalance as BankIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as DeliveredIcon,
  PeopleAlt as TeamIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import {
  loadingAlert,
  closeAlert,
  successAlert,
  errorAlert,
} from "../../utils/swalConfig";
import api from "../../utils/api";

const IMPACT_STATS = {
  homesEmpowered: "5,000+",
  businessesServed: "1,200+",
  satisfactionRate: "98%",
};

const SolarDashboard = () => {
  const [solarProgram, setSolarProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addressDialog, setAddressDialog] = useState(false);
  const [address, setAddress] = useState({
    street: "",
    lga: "",
    state: "",
    phoneNumber: "",
  });
  const [copied, setCopied] = useState("");
  const [depositReference, setDepositReference] = useState("");
  const [depositDialog, setDepositDialog] = useState(false);

  const navigate = useNavigate();

  const fetchSolarProgram = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/solar-program/status");
      if (response.data.status === "success") {
        setSolarProgram(response.data.data);
      }
      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load solar program data"
      );
      // console.error("Solar program fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSolarProgram();
  }, [fetchSolarProgram]);

  useEffect(() => {
    if (solarProgram?.program?.shippingAddress) {
      setAddress({
        street: solarProgram.program.shippingAddress.street || "",
        lga:
          solarProgram.program.shippingAddress.lga ||
          solarProgram.program.shippingAddress.city ||
          "",
        state: solarProgram.program.shippingAddress.state || "",
        phoneNumber: solarProgram.program.shippingAddress.phoneNumber || "",
      });
    }
  }, [solarProgram?.program?.shippingAddress]);

  const handleStreetChange = useCallback((e) => {
    setAddress((prev) => ({ ...prev, street: e.target.value }));
  }, []);

  const handleLgaChange = useCallback((e) => {
    setAddress((prev) => ({ ...prev, lga: e.target.value }));
  }, []);

  const handleStateChange = useCallback((e) => {
    setAddress((prev) => ({ ...prev, state: e.target.value }));
  }, []);

  const handlePhoneChange = useCallback((e) => {
    setAddress((prev) => ({ ...prev, phoneNumber: e.target.value }));
  }, []);

  const handleDepositReferenceChange = useCallback((e) => {
    setDepositReference(e.target.value);
  }, []);

  const handleAddressSubmit = useCallback(async () => {
    try {
      if (
        !address.street ||
        !address.lga ||
        !address.state ||
        !address.phoneNumber
      ) {
        return errorAlert(
          "Missing Information",
          "Please fill in all required fields"
        );
      }

      if (!/^\d{11}$/.test(address.phoneNumber)) {
        return errorAlert(
          "Invalid Phone Number",
          "Please enter a valid 11-digit phone number"
        );
      }

      loadingAlert("Submitting your shipping information...");
      await api.patch("/solar-program/shipping-address", address);
      closeAlert();
      await successAlert(
        "Congratulations!",
        "Your shipping information has been submitted. Your solar system will be delivered in 3-5 working days!"
      );
      fetchSolarProgram();
      setAddressDialog(false);
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to save shipping information"
      );
    }
  }, [address, fetchSolarProgram]);

  const handleCopy = useCallback((text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(""), 2000);
  }, []);

  const handleDepositNotification = async () => {
    try {
      await api.post("/solar-program/notify-deposit", {
        transactionReference: depositReference,
      });
      setDepositDialog(false);
      successAlert("Success", "Deposit notification sent successfully");
      fetchSolarProgram();
    } catch (err) {
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to send deposit notification"
      );
    }
  };

  const openAddressDialog = useCallback(() => {
    if (solarProgram?.program?.shippingAddress) {
      setAddress({
        street: solarProgram.program.shippingAddress.street || "",
        lga: solarProgram.program.shippingAddress.lga || "",
        state: solarProgram.program.shippingAddress.state || "",
        phoneNumber: solarProgram.program.shippingAddress.phoneNumber || "",
      });
    }
    setAddressDialog(true);
  }, [solarProgram?.program?.shippingAddress]);

  const closeAddressDialog = useCallback(() => {
    setAddressDialog(false);
  }, []);

  const openDepositDialog = useCallback(() => {
    setDepositDialog(true);
  }, []);

  const closeDepositDialog = useCallback(() => {
    setDepositDialog(false);
  }, []);

  const goToSolarReferrals = () => {
    navigate("/solar/referrals");
  };

  const goToSolarTransactions = () => {
    navigate("/solar/transactions");
  };

  const BankDetailsCard = useMemo(() => {
    if (!solarProgram) return null;

    return (
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <BankIcon sx={{ color: "success.main", mr: 1 }} />
              <Typography variant="h6">Payment Details</Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{ bgcolor: "background.default", p: 2, mb: 2 }}
            >
              <Typography variant="h5" color="primary" gutterBottom>
                ₦{solarProgram.requiredAmount.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                One-time enrollment deposit for your free solar system
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This fee covers program enrollment, installation support, and
                lifetime technical assistance.
              </Typography>
            </Paper>

            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Bank Transfer Details
              </Typography>

              <Box
                sx={{
                  bgcolor: "background.default",
                  p: 2,
                  borderRadius: 1,
                  "& > div": { mb: 2, "&:last-child": { mb: 0 } },
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Bank Name
                  </Typography>
                  <Typography variant="body1">
                    {solarProgram.adminBank.bankName}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Account Number
                    </Typography>
                    <Typography variant="body1">
                      {solarProgram.adminBank.accountNumber}
                    </Typography>
                  </Box>
                  <Tooltip
                    title={
                      copied === "account" ? "Copied!" : "Copy Account Number"
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCopy(
                          solarProgram.adminBank.accountNumber,
                          "account"
                        )
                      }
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Account Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {solarProgram.adminBank.accountName}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }} icon={false}>
              After payment, click the button below to notify us of your deposit
            </Alert>

            <Box sx={{ mt: 2, p: 2 }}>
              {solarProgram.program.depositNotified ? (
                <Alert severity="info">
                  Deposit notification sent. Awaiting admin approval.
                </Alert>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<SendIcon />}
                  onClick={openDepositDialog}
                >
                  Notify Payment
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  }, [solarProgram, copied, handleCopy, openDepositDialog]);

  const ProgressCard = useMemo(() => {
    if (!solarProgram) return null;

    if (
      solarProgram.program.isEligible &&
      !solarProgram.program.shippingAddress?.street
    ) {
      return (
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box display="flex" alignItems="center" mb={2}>
              <LocationIcon sx={{ color: "success.main", mr: 1 }} />
              <Typography
                variant="h6"
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                Congratulations! You Qualified
              </Typography>
            </Box>

            <Alert severity="success" sx={{ mb: 3 }}>
              You've successfully referred {solarProgram.requiredReferrals}{" "}
              people! Please provide your shipping details to receive your free
              1KW Solar Power System.
            </Alert>

            <Box component="form" sx={{ mt: 2 }}>
              <TextField
                margin="dense"
                label="Street Address"
                type="text"
                fullWidth
                required
                variant="outlined"
                value={address.street}
                onChange={handleStreetChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Local Government Area (LGA)"
                type="text"
                fullWidth
                required
                variant="outlined"
                value={address.lga}
                onChange={handleLgaChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="State"
                type="text"
                fullWidth
                required
                variant="outlined"
                value={address.state}
                onChange={handleStateChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Contact Phone Number"
                type="tel"
                fullWidth
                required
                variant="outlined"
                value={address.phoneNumber || ""}
                onChange={handlePhoneChange}
                sx={{ mb: 2 }}
                helperText="Please provide a valid phone number for delivery coordination"
              />
              <Button
                variant="contained"
                color="success"
                fullWidth
                size="large"
                startIcon={<LocationIcon />}
                onClick={handleAddressSubmit}
                sx={{ mt: 2 }}
                disabled={
                  !address.street ||
                  !address.lga ||
                  !address.state ||
                  !address.phoneNumber
                }
              >
                Submit Shipping Information
              </Button>
            </Box>
          </CardContent>
        </Card>
      );
    } else if (
      solarProgram.program.isEligible &&
      solarProgram.program.shippingAddress?.street
    ) {
      return (
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box display="flex" alignItems="center" mb={2}>
              <SolarIcon sx={{ color: "success.main", mr: 1 }} />
              <Typography
                variant="h6"
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                Solar Package Status
              </Typography>
            </Box>

            {/* Make the Timeline more compact on mobile */}
            <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
              Shipping Status Timeline
            </Typography>

            <Timeline
              position="alternate"
              sx={{
                mb: 2,
                p: 0,
                [`& .MuiTimelineItem-root`]: {
                  minHeight: { xs: "60px", sm: "70px" },
                },
                [`& .MuiTimelineContent-root`]: {
                  py: { xs: "8px", sm: "12px" },
                  px: { xs: 1, sm: 2 },
                },
              }}
            >
              <TimelineItem>
                <TimelineOppositeContent sx={{ m: "auto 0" }}>
                  <Typography variant="body2" color="text.secondary">
                    {solarProgram.program.createdAt
                      ? new Date(
                          solarProgram.program.createdAt
                        ).toLocaleDateString() || "Date not available"
                      : "Date not available"}
                  </Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot color="success" />
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ py: "12px", px: 2 }}>
                  <Typography variant="h6" component="span">
                    Qualified
                  </Typography>
                  <Typography variant="body2">
                    You've met all requirements
                  </Typography>
                </TimelineContent>
              </TimelineItem>

              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot color="success" />
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ py: "12px", px: 2 }}>
                  <Typography variant="h6" component="span">
                    Address Submitted
                  </Typography>
                  <Typography variant="body2">
                    Shipping information provided
                  </Typography>
                </TimelineContent>
              </TimelineItem>

              <TimelineItem>
                <TimelineOppositeContent sx={{ m: "auto 0" }}>
                  <Typography variant="body2" color="text.secondary">
                    {solarProgram.program.status === "shipped" ||
                    solarProgram.program.status === "delivered"
                      ? new Date(
                          solarProgram.program.shippingDate
                        ).toLocaleDateString()
                      : "In progress"}
                  </Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot
                    color={
                      solarProgram.program.status === "shipped" ||
                      solarProgram.program.status === "delivered"
                        ? "primary"
                        : "grey"
                    }
                    variant={
                      solarProgram.program.status === "shipped" ||
                      solarProgram.program.status === "delivered"
                        ? "default"
                        : "outlined"
                    }
                  >
                    <ShippingIcon />
                  </TimelineDot>
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ py: "12px", px: 2 }}>
                  <Typography variant="h6" component="span">
                    Shipped
                  </Typography>
                  <Typography variant="body2">
                    {solarProgram.program.status === "shipped" ||
                    solarProgram.program.status === "delivered"
                      ? "Your package is on its way"
                      : "Awaiting shipment"}
                  </Typography>
                </TimelineContent>
              </TimelineItem>

              <TimelineItem>
                <TimelineOppositeContent sx={{ m: "auto 0" }}>
                  <Typography variant="body2" color="text.secondary">
                    {solarProgram.program.status === "delivered"
                      ? new Date(
                          solarProgram.program.deliveryDate
                        ).toLocaleDateString()
                      : solarProgram.program.estimatedDelivery
                      ? `Est. ${new Date(
                          solarProgram.program.estimatedDelivery
                        ).toLocaleDateString()}`
                      : "Pending"}
                  </Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot
                    color={
                      solarProgram.program.status === "delivered"
                        ? "success"
                        : "grey"
                    }
                    variant={
                      solarProgram.program.status === "delivered"
                        ? "default"
                        : "outlined"
                    }
                  >
                    <DeliveredIcon />
                  </TimelineDot>
                </TimelineSeparator>
                <TimelineContent sx={{ py: "12px", px: 2 }}>
                  <Typography variant="h6" component="span">
                    Delivered
                  </Typography>
                  <Typography variant="body2">
                    {solarProgram.program.status === "delivered"
                      ? "Package successfully delivered"
                      : "Awaiting delivery"}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            </Timeline>

            {/* Rest of the content with improved spacing */}
            <Alert
              severity={
                solarProgram.program.status === "delivered"
                  ? "success"
                  : solarProgram.program.status === "shipped"
                  ? "info"
                  : "warning"
              }
              sx={{ mb: 3 }}
            >
              {solarProgram.program.status === "delivered"
                ? "Your solar system has been delivered! Enjoy your renewable energy solution."
                : solarProgram.program.status === "shipped"
                ? `Your solar system is on its way! Expected delivery by ${new Date(
                    solarProgram.program.estimatedDelivery
                  ).toLocaleDateString()}.`
                : "Your solar system will be shipped soon. Our team will contact you for delivery coordination."}
            </Alert>
            <Paper
              elevation={0}
              sx={{ bgcolor: "background.default", p: 2, borderRadius: 1 }}
            >
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Shipping Address:
              </Typography>
              <Typography variant="body2">
                {solarProgram.program.shippingAddress.street}
              </Typography>
              <Typography variant="body2">
                {solarProgram.program.shippingAddress.lga},{" "}
                {solarProgram.program.shippingAddress.state}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Contact Phone:{" "}
                {solarProgram.program.shippingAddress.phoneNumber ||
                  "Not provided"}
              </Typography>

              {solarProgram.program.trackingNumber && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.primary"
                    gutterBottom
                  >
                    Tracking Information:
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2">
                      Tracking Number: {solarProgram.program.trackingNumber}
                    </Typography>
                    <Tooltip title="Copy Tracking Number">
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleCopy(
                            solarProgram.program.trackingNumber,
                            "tracking"
                          )
                        }
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}

              {solarProgram.program.shippingNotes && (
                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="subtitle2">
                    Notes from Shipping Team:
                  </Typography>
                  <Typography variant="body2">
                    {solarProgram.program.shippingNotes}
                  </Typography>
                </Alert>
              )}

              <Button
                variant="outlined"
                startIcon={<LocationIcon />}
                onClick={openAddressDialog}
                sx={{ mt: 2 }}
                size="small"
              >
                Update Shipping Information
              </Button>
            </Paper>
          </CardContent>
        </Card>
      );
    } else {
      return (
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box display="flex" alignItems="center" mb={2}>
              <ReferralIcon sx={{ color: "info.main", mr: 1 }} />
              <Typography
                variant="h6"
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                Your Progress
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                Valid Referral Progress ({solarProgram.program.referralCount}/
                {solarProgram.requiredReferrals})
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  (solarProgram.program.referralCount /
                    solarProgram.requiredReferrals) *
                  100
                }
                sx={{ height: { xs: 8, sm: 10 }, borderRadius: 5 }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  mt: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {solarProgram.program.qualifiedReferrals} more valid referrals
                  needed
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: { xs: 0.5, sm: 0 } }}
                >
                  Total Referrals: {solarProgram.stats.totalReferrals} (Pending:{" "}
                  {solarProgram.stats.pendingReferrals})
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      );
    }
  }, [
    solarProgram,
    address.street,
    address.lga,
    address.state,
    address.phoneNumber,
    handleStreetChange,
    handleLgaChange,
    handleStateChange,
    handlePhoneChange,
    handleAddressSubmit,
    handleCopy,
    openAddressDialog,
  ]);

  const ShippingUpdatesCard = useMemo(() => {
    if (
      !solarProgram ||
      !solarProgram.program ||
      !solarProgram.program.isEligible ||
      !solarProgram.program.hasDeposited ||
      !solarProgram.program.depositApproved ||
      !solarProgram.program.shippingAddress?.street
    ) {
      return null;
    }

    return (
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <ShippingIcon sx={{ color: "primary.main", mr: 1 }} />
              <Typography variant="h6">Delivery Updates</Typography>
              <IconButton
                size="small"
                sx={{ ml: "auto" }}
                onClick={fetchSolarProgram}
                title="Refresh"
              >
                <RefreshIcon />
              </IconButton>
            </Box>

            {solarProgram.program.status === "pending" && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Processing Your Order</AlertTitle>
                Your solar system is being prepared for shipping. You'll receive
                updates when it ships.
              </Alert>
            )}

            {solarProgram.program.status === "shipped" && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Package In Transit</AlertTitle>
                Your solar system has been shipped on{" "}
                {new Date(
                  solarProgram.program.shippingDate
                ).toLocaleDateString()}
                . Expected delivery:{" "}
                {new Date(
                  solarProgram.program.estimatedDelivery
                ).toLocaleDateString()}
                .
              </Alert>
            )}

            {solarProgram.program.status === "delivered" && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <AlertTitle>Package Delivered</AlertTitle>
                Your solar system was delivered on{" "}
                {new Date(
                  solarProgram.program.deliveryDate
                ).toLocaleDateString()}
                . We hope you're enjoying your new renewable energy solution!
              </Alert>
            )}

            <Paper
              elevation={0}
              sx={{
                bgcolor: "background.default",
                p: 2,
                borderRadius: 1,
                mt: 2,
              }}
            >
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Need Help?
              </Typography>
              <Typography variant="body2">
                If you have any questions or need assistance with your solar
                system, please contact our support team:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Email: support@solarmarger.com
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                We're available Monday to Friday, 9 AM - 5 PM
              </Typography>
            </Paper>
          </CardContent>
        </Card>
      </Grid>
    );
  }, [solarProgram, fetchSolarProgram]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!solarProgram || !solarProgram.program) {
    return (
      <Box p={3}>
        <Alert severity="info">Solar program data not available</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          mb: { xs: 2, md: 4 },
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            position: "relative",
            fontWeight: 700,
            color: "primary.main",
            mb: 1,
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
            "&:after": {
              content: '""',
              position: "absolute",
              bottom: -8,
              left: 0,
              width: 60,
              height: 4,
              borderRadius: 2,
              backgroundColor: "primary.main",
            },
          }}
        >
          Own A Solar Program
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{
            mt: 2,
            fontSize: { xs: "0.875rem", sm: "1rem" },
          }}
        >
          Get a free 1KW Solar Power System by completing simple qualification
          steps
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box display="flex" alignItems="center" mb={2}>
                <SolarIcon sx={{ color: "primary.main", mr: 1 }} />
                <Typography
                  variant="h6"
                  sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                >
                  Program Overview
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                Get a free 1KW Solar Power System perfect for homes and small
                businesses. Power essential appliances and reduce energy costs.
              </Alert>

              <Typography
                variant="subtitle1"
                gutterBottom
                mt={2}
                fontWeight="medium"
                sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
              >
                Qualification Criteria:
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  bgcolor: "background.default",
                  p: { xs: 1.5, sm: 2 },
                  mb: 3,
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                    <Box
                      component="span"
                      sx={{
                        width: { xs: 20, sm: 22 },
                        height: { xs: 20, sm: 22 },
                        minWidth: { xs: 20, sm: 22 },
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1.5,
                        fontSize: "0.8rem",
                        fontWeight: "bold",
                      }}
                    >
                      1
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                    >
                      Make a <strong>one-time deposit of ₦6,000</strong> for
                      declarations of interest.
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                    <Box
                      component="span"
                      sx={{
                        width: { xs: 20, sm: 22 },
                        height: { xs: 20, sm: 22 },
                        minWidth: { xs: 20, sm: 22 },
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1.5,
                        fontSize: "0.8rem",
                        fontWeight: "bold",
                      }}
                    >
                      2
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                    >
                      Refer{" "}
                      <strong>{solarProgram.requiredReferrals} friends</strong>{" "}
                      who register and activate their accounts
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                    <Box
                      component="span"
                      sx={{
                        width: { xs: 20, sm: 22 },
                        height: { xs: 20, sm: 22 },
                        minWidth: { xs: 20, sm: 22 },
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1.5,
                        fontSize: "0.8rem",
                        fontWeight: "bold",
                      }}
                    >
                      3
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                    >
                      Provide a valid shipping address for delivery (shipping
                      form will be display after step 1 & 2 are completed)
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Box
                sx={{
                  overflowX: { xs: "auto", sm: "visible" },
                  pb: 1, // Space for scrollbar
                }}
              >
                <Grid
                  container
                  spacing={1}
                  sx={{
                    minWidth: { xs: "500px", sm: "100%" },
                  }}
                >
                  {Object.entries(IMPACT_STATS).map(([key, value]) => (
                    <Grid item xs={4} key={key}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: { xs: 1, sm: 2 },
                        }}
                      >
                        <Typography
                          variant="h4"
                          color="primary"
                          gutterBottom
                          sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
                        >
                          {value}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                        >
                          {key
                            .replace(/([A-Z])/g, " $1")
                            .charAt(0)
                            .toUpperCase() +
                            key.replace(/([A-Z])/g, " $1").slice(1)}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          {ProgressCard}
        </Grid>

        <Grid item xs={12} md={6}>
          {solarProgram && (
            <Card>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <BankIcon sx={{ color: "success.main", mr: 1 }} />
                  <Typography
                    variant="h6"
                    sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                  >
                    Payment Details
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{ bgcolor: "background.default", p: 2, mb: 2 }}
                >
                  <Typography variant="h5" color="primary" gutterBottom>
                    ₦{solarProgram.requiredAmount?.toLocaleString() || "6,000"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    One-time enrollment deposit for your free solar system
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    This fee covers program enrollment, installation support,
                    and lifetime technical assistance.
                  </Typography>
                </Paper>

                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Bank Transfer Details
                  </Typography>

                  <Box
                    sx={{
                      bgcolor: "background.default",
                      p: 2,
                      borderRadius: 1,
                      "& > div": { mb: 2, "&:last-child": { mb: 0 } },
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Bank Name
                      </Typography>
                      <Typography variant="body1">
                        {solarProgram.adminBank?.bankName ||
                          "Bank information unavailable"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Account Number
                        </Typography>
                        <Typography variant="body1">
                          {solarProgram.adminBank?.accountNumber || "N/A"}
                        </Typography>
                      </Box>
                      {solarProgram.adminBank?.accountNumber && (
                        <Tooltip
                          title={
                            copied === "account"
                              ? "Copied!"
                              : "Copy Account Number"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleCopy(
                                solarProgram.adminBank.accountNumber,
                                "account"
                              )
                            }
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Account Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {solarProgram.adminBank?.accountName || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }} icon={false}>
                  After payment, click the button below to notify us of your
                  deposit
                </Alert>

                <Box sx={{ mt: 2, p: 2 }}>
                  {solarProgram.program.depositNotified ? (
                    <Alert severity="info">
                      Deposit notification sent. Awaiting admin approval.
                    </Alert>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      startIcon={<SendIcon />}
                      onClick={openDepositDialog}
                    >
                      Notify Payment
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              startIcon={<TeamIcon />}
              onClick={goToSolarReferrals}
              fullWidth
            >
              View All Referrals
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<HistoryIcon />}
              onClick={goToSolarTransactions}
              fullWidth
            >
              Transaction History
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          {ShippingUpdatesCard}
        </Grid>
      </Grid>

      <Dialog
        open={addressDialog}
        onClose={closeAddressDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Update Shipping Address</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Street Address"
            type="text"
            fullWidth
            required
            variant="outlined"
            value={address.street}
            onChange={handleStreetChange}
          />
          <TextField
            margin="dense"
            label="Local Government Area (LGA)"
            type="text"
            fullWidth
            required
            variant="outlined"
            value={address.lga}
            onChange={handleLgaChange}
          />
          <TextField
            margin="dense"
            label="State"
            type="text"
            fullWidth
            required
            variant="outlined"
            value={address.state}
            onChange={handleStateChange}
          />
          <TextField
            margin="dense"
            label="Contact Phone Number"
            type="tel"
            fullWidth
            required
            variant="outlined"
            value={address.phoneNumber || ""}
            onChange={handlePhoneChange}
            helperText="Please provide a valid phone number for delivery coordination"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddressDialog}>Cancel</Button>
          <Button
            onClick={handleAddressSubmit}
            variant="contained"
            color="primary"
            disabled={
              !address.street ||
              !address.lga ||
              !address.state ||
              !address.phoneNumber
            }
          >
            Update Address
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={depositDialog}
        onClose={closeDepositDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Notify Deposit Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter the transaction reference or receipt number from your
            bank payment.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Transaction Reference"
            fullWidth
            variant="outlined"
            value={depositReference}
            onChange={handleDepositReferenceChange}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDepositDialog}>Cancel</Button>
          <Button
            onClick={handleDepositNotification}
            variant="contained"
            color="primary"
            disabled={!depositReference.trim()}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SolarDashboard;
